'use client';

import React from 'react';
import AppImage from '@/components/ui/AppImage';
import { Users, Wind, CheckCircle } from 'lucide-react';
import type { VehicleOption } from './TaxiBookingClient';

interface VehicleSelectorProps {
  vehicles: VehicleOption[];
  selectedVehicle: VehicleOption | null;
  onSelect: (vehicle: VehicleOption) => void;
}

export default function VehicleSelector({
  vehicles,
  selectedVehicle,
  onSelect,
}: VehicleSelectorProps) {
  return (
    <div className="bg-white rounded-2xl border border-border shadow-sm p-6">
      <h2 className="font-bold text-foreground text-lg mb-2">Select Vehicle</h2>
      <p className="text-sm text-muted-foreground mb-5">
        Choose based on group size and comfort preference
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {vehicles.map((vehicle) => {
          const isSelected = selectedVehicle?.id === vehicle.id;
          return (
            <button
              key={vehicle.id}
              type="button"
              onClick={() => onSelect(vehicle)}
              className={`vehicle-card text-left relative transition-all duration-180 ${
                isSelected ? 'active' : ''
              }`}
            >
              {isSelected && (
                <div className="absolute top-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                  <CheckCircle size={14} className="text-white fill-white" />
                </div>
              )}
              {vehicle.badge && (
                <span className="absolute top-2 left-2 badge-primary text-xs">
                  {vehicle.badge}
                </span>
              )}

              <AppImage
                src={vehicle.image}
                alt={vehicle.alt}
                width={220}
                height={110}
                className="w-full h-28 object-cover rounded-xl mb-3"
              />

              <div className="font-bold text-foreground text-sm">{vehicle.name}</div>
              <div className="text-xs text-muted-foreground mb-2">{vehicle.model}</div>

              <div className="flex items-center justify-between text-xs mb-2">
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Users size={12} />
                  {vehicle.capacity} seats
                </span>
                {vehicle.ac && (
                  <span className="flex items-center gap-1 text-secondary font-semibold">
                    <Wind size={12} />
                    AC
                  </span>
                )}
              </div>

              <div className="pt-2 border-t border-border flex items-center justify-between">
                <div>
                  <div className="text-xs text-muted-foreground">Base fare</div>
                  <div className="text-primary font-bold text-sm font-tabular">
                    ₹{vehicle.baseFare.toLocaleString('en-IN')}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-muted-foreground">Per km</div>
                  <div className="text-foreground font-semibold text-sm font-tabular">
                    ₹{vehicle.perKm}/km
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}